import { useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Sparkles, Copy, X, Save, Loader2 } from "lucide-react";
import { useBilling } from "../context/BillingContext";

const OCCASIONS = [
  { k: "check_in", l: "Check-in" },
  { k: "birthday", l: "Birthday" },
  { k: "thank_you", l: "Thank you" },
  { k: "milestone", l: "Milestone / win" },
  { k: "follow_up", l: "Follow-up" },
  { k: "holiday", l: "Holiday" },
];

export default function PersonalNoteModal({ open, onClose, personKind, person }) {
  const { handleApiError, canUse, requireUpgrade } = useBilling();
  const [occasion, setOccasion] = useState("check_in");
  const [extra, setExtra] = useState("");
  const [busy, setBusy] = useState(false);
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);
  const [savedId, setSavedId] = useState(null);

  if (!open || !person) return null;

  const generate = async () => {
    if (!canUse("personal_notes")) {
      onClose();
      requireUpgrade({
        message: "Personalized Notes are part of Executive. Upgrade to draft on-brand notes for every team member and customer in one click.",
        feature: "personal_notes",
      });
      return;
    }
    setBusy(true); setText(""); setSavedId(null);
    try {
      const { data } = await api.post("/personal-note/generate", {
        person_kind: personKind,
        person_id: person.id,
        occasion,
        extra_instructions: extra,
        save_to_library: false,
      });
      setText(data.text);
    } catch (e) {
      if (!handleApiError(e)) toast.error("Couldn't draft the note");
    } finally { setBusy(false); }
  };

  const copy = async () => {
    await navigator.clipboard.writeText(text);
    toast.success("Copied");
  };

  const saveToLibrary = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/personal-note/generate", {
        person_kind: personKind,
        person_id: person.id,
        occasion,
        extra_instructions: extra + "\n\n[Use this exact text:]\n" + text,
        save_to_library: true,
      });
      // Re-use returned text if backend regenerates with extra instructions
      if (data?.saved_id) setSavedId(data.saved_id);
      toast.success("Saved to Content library");
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-zinc-900/60 z-50 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white border border-zinc-200 w-full max-w-2xl" onClick={(e)=>e.stopPropagation()} data-testid="personal-note-modal">
        <div className="border-b border-zinc-200 p-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-[#0033FF] flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-white" />
            </div>
            <div>
              <div className="text-[10px] tracking-[0.25em] uppercase text-[#0033FF] font-bold">Personalized note</div>
              <div className="text-lg font-black tracking-tighter">For {person.name}</div>
            </div>
          </div>
          <button onClick={onClose} className="text-zinc-400 hover:text-zinc-900" data-testid="note-close"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-5 space-y-4">
          <div>
            <label className="text-[10px] tracking-[0.25em] uppercase font-bold text-zinc-600">Occasion</label>
            <div className="mt-2 flex flex-wrap gap-1">
              {OCCASIONS.map((o) => (
                <button key={o.k} data-testid={`note-occasion-${o.k}`} onClick={()=>setOccasion(o.k)}
                  className={`px-3 py-2 text-xs font-bold tracking-wider uppercase border ${occasion===o.k ? "bg-zinc-900 text-white border-zinc-900" : "border-zinc-300 hover:border-zinc-900"}`}>
                  {o.l}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-[10px] tracking-[0.25em] uppercase font-bold text-zinc-600">Anything else? (optional)</label>
            <textarea data-testid="note-extra" value={extra} onChange={(e)=>setExtra(e.target.value)}
              className="w-full mt-2 px-3 py-2.5 border border-zinc-300 focus:outline-none focus:border-[#0033FF] text-sm min-h-[70px]"
              placeholder="e.g. reference the launch last week, keep it under 80 words" />
          </div>

          {!text && (
            <button onClick={generate} disabled={busy} data-testid="note-generate"
              className="w-full bg-[#0033FF] text-white px-4 py-3 text-xs font-bold uppercase tracking-wider hover:bg-zinc-900 disabled:opacity-50 inline-flex items-center justify-center gap-2">
              {busy ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Drafting…</> : <><Sparkles className="w-3.5 h-3.5" /> Draft the note</>}
            </button>
          )}

          {text && (
            <div className="border border-zinc-200">
              <textarea data-testid="note-text" value={text} onChange={(e)=>setText(e.target.value)}
                className="w-full p-4 min-h-[260px] text-sm leading-relaxed focus:outline-none" />
              <div className="border-t border-zinc-200 p-3 flex flex-wrap gap-2 justify-end">
                <button onClick={generate} disabled={busy} data-testid="note-redraft"
                  className="border border-zinc-300 px-3 py-2 text-xs font-bold uppercase tracking-wider hover:bg-zinc-100 disabled:opacity-50 inline-flex items-center gap-2">
                  {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />} Redraft
                </button>
                <button onClick={copy} data-testid="note-copy"
                  className="border border-zinc-300 px-3 py-2 text-xs font-bold uppercase tracking-wider hover:bg-zinc-100 inline-flex items-center gap-2">
                  <Copy className="w-3 h-3" /> Copy
                </button>
                <button onClick={saveToLibrary} disabled={saving || !!savedId} data-testid="note-save"
                  className="bg-[#0033FF] text-white px-3 py-2 text-xs font-bold uppercase tracking-wider hover:bg-zinc-900 disabled:opacity-50 inline-flex items-center gap-2">
                  <Save className="w-3 h-3" /> {savedId ? "Saved" : saving ? "Saving…" : "Save to library"}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
